/**
 * A geometria do enquadramento — só as contas.
 *
 * Separada da tela (`Enquadrar.tsx`) por um motivo prático: assim ela
 * pode ser TESTADA sem navegador, sem câmera e sem foto. O que este
 * arquivo garante é a única coisa que o recorte precisa garantir — que a
 * janela lida do arquivo é EXATAMENTE o quadrado que a pessoa viu na
 * tela, nem um pixel a mais.
 *
 * ─── O modelo ────────────────────────────────────────────────────────
 *
 * A imagem é desenhada centrada num quadro quadrado de lado `L`,
 * deslocada de (dx, dy) e ampliada por `e`. Um ponto (px, py) da imagem
 * cai no ponto do quadro
 *
 *     cx = L/2 + dx + (px − w/2)·e
 *
 * Invertendo nas duas bordas (cx = 0 e cx = L) sai a janela: lado L/e,
 * começando em sx = w/2 − (L/2 + dx)/e.
 *
 * `e` nunca é menor que `max(L/w, L/h)` — a ampliação que cobre o
 * quadro. Abaixo dela sobraria vazio dentro da moldura, e o recorte
 * traria borda preta para dentro do laudo.
 */

export const entre = (v: number, min: number, max: number): number =>
  (max < min ? (min + max) / 2 : Math.min(max, Math.max(min, v)));

/** A ampliação mínima: a que faz a imagem cobrir o quadro inteiro. */
export function escalaQueCobre(lado: number, w: number, h: number): number {
  return Math.max(lado / w, lado / h);
}

/**
 * Até onde o deslocamento pode ir sem descobrir a moldura.
 *
 * |dx| ≤ (w·e − L)/2. Com `e` ≥ escalaQueCobre isso nunca é negativo;
 * o `max(0, …)` é cinto de segurança para o caso de alguém chamar com
 * uma escala menor.
 */
export function travaDoDeslocamento(
  lado: number, tamanho: number, escala: number,
): number {
  return Math.max(0, (tamanho * escala - lado) / 2);
}

/** Onde um ponto da imagem cai no quadro. */
export function pontoNaTela(
  lado: number, tamanho: number, escala: number, desloc: number, p: number,
): number {
  return lado / 2 + desloc + (p - tamanho / 2) * escala;
}

export interface Enquadramento {
  lado: number; w: number; h: number; escala: number; dx: number; dy: number;
}

/** A janela lida do arquivo, em pixels do arquivo. */
export function janelaDoRecorte(q: Enquadramento): {
  sx: number; sy: number; tamanho: number;
} {
  return {
    sx: q.w / 2 - (q.lado / 2 + q.dx) / q.escala,
    sy: q.h / 2 - (q.lado / 2 + q.dy) / q.escala,
    tamanho: q.lado / q.escala,
  };
}

/**
 * Ampliar em torno de um ponto do quadro — o meio da pinça, o cursor.
 *
 * O ponto da imagem que está debaixo do dedo tem de continuar debaixo do
 * dedo. Sem isto, a pinça amplia em torno do centro do quadro e o corpo
 * de prova foge para fora da moldura enquanto a pessoa tenta ampliá-lo.
 */
export function ampliarEmTorno(
  lado: number, z: number, dx: number, dy: number,
  fator: number, mx: number, my: number, zmax: number,
): { z: number; dx: number; dy: number } {
  const nz = entre(z * fator, 1, zmax);
  const real = nz / z;
  return {
    z: nz,
    dx: mx - lado / 2 - (mx - lado / 2 - dx) * real,
    dy: my - lado / 2 - (my - lado / 2 - dy) * real,
  };
}
